import { ReactNode } from 'react'

import { useSignupPageUpdate } from '@/services/onboardingService'
import { getCurrentUser, selectHasAppAccess } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { canEnterComplianceFunnel } from '@/features/user/signup/helpers'

import { FunnelHeader } from './components/FunnelHeader'
import { COMPLIANCE_PATHS } from './lib/constants'

type CompliancePath = (typeof COMPLIANCE_PATHS)[keyof typeof COMPLIANCE_PATHS]

/**
 * The funnel's route-level shell: records the funnel position for a pre-purchase user,
 * and frames the screen with the funnel's own header.
 *
 * Both live at the route rather than inside the intake/report components because those
 * are slated for reuse as in-app pages — an entitled user must never have their
 * onboarding position rewritten to a funnel screen, and reused in-app those screens
 * take the product's sidebar, not a wordmark-and-logout bar of their own.
 */
export const CompliancePositionRoute = ({
  screen,
  children,
}: {
  screen: CompliancePath
  children: ReactNode
}) => {
  const userHasAppAccess = useReselector(selectHasAppAccess)
  const currentUser = useReselector(getCurrentUser)
  const storedScreen = currentUser?.lastOnboardingScreen

  const shouldRecord =
    userHasAppAccess === false && canEnterComplianceFunnel(storedScreen)

  useSignupPageUpdate(screen, !shouldRecord)

  return (
    <div className="flex w-full flex-col gap-6">
      <FunnelHeader />
      {children}
    </div>
  )
}
